import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Droplets, Flame, ShieldAlert, Hammer, Waves, Wrench, Clock, FileCheck, MapPin, ArrowRight, ShieldCheck, Building2, PhoneCall } from "lucide-react";
interface ServiceItem {
  title: string;
  description: string;
  to?: string;
  icon: React.ElementType;
  badge?: string;
}
const services: ServiceItem[] = [{
  title: "Water Damage Restoration",
  description: "Older residential plumbing systems in communities near the DC line and multi-family buildings along the Route 1 and I-95 corridors experience sudden pipe ruptures, slab leaks, and water heater failures. TriServe deploys thermal infrared imaging, moisture mapping, and commercial dehumidification systems to dry hidden framing and subfloors before permanent structural deterioration sets in.",
  to: "/WaterDamageFloodRestorationPage",
  icon: Droplets,
  badge: "60-Min Deployment"
}, {
  title: "24/7 Emergency Flood Cleanup",
  description: "Severe rainfall and seasonal storms across the Patuxent and Anacostia watersheds frequently overwhelm storm drainage and cause rapid groundwater intrusion in lower-level Prince George's County properties. Our crews deploy heavy-duty submersible pumps, weighted extraction systems, and desiccant dryers to extract standing floodwater and stabilize concrete slabs.",
  icon: Waves,
  badge: "24/7 Rapid Extraction"
}, {
  title: "Sewage Backup Cleanup",
  description: "Municipal main line surcharges, root intrusions, and ejector pump failures in established Prince George's County neighborhoods create hazardous Category 3 blackwater conditions. We execute immediate containment, sanitary extraction, disposal of non-salvageable porous drywall and carpet, and hospital-grade EPA-registered antimicrobial disinfection.",
  to: "/SewageCleanupPage",
  icon: ShieldAlert,
  badge: "Biohazard Protocol"
}, {
  title: "Fire & Smoke Damage Restoration",
  description: "From kitchen grease fires in mid-century homes to commercial property blazes along Route 50, toxic soot and acidic residue spread rapidly through HVAC ductwork and structural timber. We deliver 24/7 emergency site security, board-up, HEPA air scrubbing, thermal fogging, and deep soot decontamination to eliminate smoke odors at the source.",
  to: "/FireSmokeOdorRestorationPage",
  icon: Flame,
  badge: "Thermal Decontamination"
}, {
  title: "Mold Remediation",
  description: "Persistent Mid-Atlantic humidity coupled with damp, unsealed basements in older housing stock near the DC border fosters active fungal growth behind sheetrock, subflooring, and crawlspaces. We erect negative-pressure poly containment zones, operate HEPA filtration units, and apply EPA-registered antimicrobials to eliminate colonies and mitigate moisture sources.",
  to: "/MoldRemediationPage",
  icon: ShieldCheck,
  badge: "Containment & Abatement"
}, {
  title: "Reconstruction & Property Repairs",
  description: "When structural tear-out is necessary following water, fire, or sewage losses, TriServe transitions smoothly from containment to full rebuilding. Our carpentry and finish trades handle structural framing, drywall replacement, insulation, and interior finishes back to pre-loss condition, preparing itemized Xactimate estimates directly for your insurance adjuster.",
  to: "/ReconstructionDesignBuildPage",
  icon: Hammer,
  badge: "Design & Build"
}, {
  title: "General Emergency Property Restoration",
  description: "Severe storm systems, tree impacts on rooflines, and sudden mechanical failures require prompt structural stabilization throughout Prince George's County. We deploy 24/7 with emergency roof tarping, structural shoring, window board-up, and mobile power generation to protect your building against secondary weather intrusion.",
  icon: Wrench,
  badge: "24/7 Stabilization"
}];
export default function PrinceGeorgesServices() {
  return <section id="princegeorgesservicesoverview" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-semibold text-primary border-primary/30">
            <MapPin className="size-3.5" />
            Prince George's County Coverage
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
            Disaster restoration and emergency repairs across Prince George's County
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When sudden water intrusion, structural fire, sewage backup, or hidden mold threatens your property in Prince George's County,
            delays increase secondary damage. TriServe dispatches local crews directly from strategically positioned mobile
            units along major transit corridors, ensuring an average arrival window of approximately 60 minutes with no call center delays.
          </p>
        </div>

        {/* Operating Pillars Banner */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-start gap-3.5 rounded-lg border border-border bg-card p-4 shadow-2xs">
            <Clock className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-card-foreground">~60-minute local deployment</p>
              <p className="text-xs text-muted-foreground mt-0.5">Rapid mobile dispatch directly to Prince George's County properties.</p>
            </div>
          </div>

          <div className="flex items-start gap-3.5 rounded-lg border border-border bg-card p-4 shadow-2xs">
            <Building2 className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-card-foreground">No call centers</p>
              <p className="text-xs text-muted-foreground mt-0.5">Direct communication with field crews who manage your restoration.</p>
            </div>
          </div>

          <div className="flex items-start gap-3.5 rounded-lg border border-border bg-card p-4 shadow-2xs">
            <FileCheck className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-card-foreground">Direct insurance coordination</p>
              <p className="text-xs text-muted-foreground mt-0.5">Itemized Xactimate billing submitted directly to your adjuster.</p>
            </div>
          </div>

          <div className="flex items-start gap-3.5 rounded-lg border border-border bg-card p-4 shadow-2xs">
            <MapPin className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-card-foreground">Mobile dispatch model</p>
              <p className="text-xs text-muted-foreground mt-0.5">Fully stocked vehicle units; no walk-in office in Maryland.</p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="mt-14 sm:mt-16">
          <div className="mb-8">
            <p className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Complete emergency restoration & property remediation services
            </p>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Every deployment begins with thermal imaging, precision moisture detection, and industrial containment to arrest structural decay and restore safety.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
            const Icon = service.icon;
            const isFullWidthCard = service.title === "General Emergency Property Restoration";
            if (isFullWidthCard) {
              return <Card key={service.title} data-index={index} className="md:col-span-2 lg:col-span-3 border-border bg-card shadow-xs hover:shadow-md transition-shadow py-6">
                    <CardContent className="px-6">
                      <div className="flex flex-col md:flex-row gap-6 items-center">
                        <div className="w-full md:w-1/2 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                <Icon className="size-5" />
                              </div>
                              {service.badge && <span className="text-[11px] font-medium tracking-wide uppercase px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                                  {service.badge}
                                </span>}
                            </div>
                            <h3 className="text-lg font-bold text-card-foreground mb-3">
                              {service.title}
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                              {service.description}
                            </p>
                          </div>
                          <div className="pt-2 border-t border-border/60">
                            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                              <span className="size-1.5 rounded-full bg-primary inline-block" />
                              On-site assessment & urgent containment
                            </span>
                          </div>
                        </div>
                        <div className="w-full md:w-1/2 h-64 sm:h-72 md:h-80 rounded-lg border border-border overflow-hidden">
                          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_equipment_pipe_rupture.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_equipment_pipe_rupture.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/emergency_restoration_equipment_pipe_rupture.png-wvc-sizes" alt="TriServe restoration technicians performing emergency water extraction and pipe rupture cleanup with professional drying equipment in a Prince George's County, MD property" width={600} height={400} loading="lazy" decoding="async" className="w-full h-full object-cover rounded-lg" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>;
            }
            return <Card key={service.title} data-index={index} className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="size-5" />
                      </div>
                      {service.badge && <span className="text-[11px] font-medium tracking-wide uppercase px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                          {service.badge}
                        </span>}
                    </div>
                    <h3 className="text-lg font-bold text-card-foreground">
                      {service.title}
                    </h3>
                  </CardHeader>
                  <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                      {service.description}
                    </p>
                    {service.to ? <div>
                        <Button asChild variant="outline" size="sm" className="w-full justify-between group text-foreground hover:text-primary hover:border-primary/40">
                          <Link to={service.to}>
                            <span>View {service.title}</span>
                            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                          </Link>
                        </Button>
                      </div> : <div className="pt-2 border-t border-border/60">
                        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <span className="size-1.5 rounded-full bg-primary inline-block" />
                          On-site assessment & urgent containment
                        </span>
                      </div>}
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Emergency Assistance & Direct Dispatch Banner */}
        <div className="mt-14 sm:mt-16 rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1.5 text-center sm:text-left">
            <p className="text-lg font-bold text-card-foreground">
              Need immediate emergency restoration in Prince George's County?
            </p>
            <p className="text-sm text-muted-foreground">
              Call our 24/7 direct line at{" "}
              <a href="tel:8554356631" className="font-semibold text-primary hover:underline inline-flex items-center gap-1">
                <PhoneCall className="size-3.5 inline" />
                855-435-6631
              </a>{" "}
              for ~60-minute local mobile deployment and direct insurance adjuster coordination.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="default" className="w-full sm:w-auto">
              <Link to="/ContactRequestServicePage">
                Request Emergency Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full sm:w-auto">
              <Link to="/OurProcessPage">
                Our Process
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}